import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import type { ScatterShapeProps } from 'recharts';
import {
  AlertTriangle,
  CircleAlert,
  CircleCheck,
  FlaskConical,
  OctagonAlert,
  PencilLine,
  RotateCcw,
  SlidersHorizontal,
} from 'lucide-react';
import { useTheme } from '@/contexts/theme-context';
import { useAuth } from '@/contexts/auth-hook';
import { PainelCard } from './painel-card';
import { obterVizTheme } from './squad-viz-theme';
import {
  Decisao,
  FAIXAS,
  PERIODOS,
  PERIODO_ATUAL,
  PESOS_PADRAO,
  PILARES,
  Periodo,
  Pesos,
  SQUADS_DEMO,
  faixaDoIndice,
  formatarMoeda,
  formatarMoedaCompacta,
  montarHistorico,
  montarVisao,
  obterDecisoes,
  obterPesos,
  salvarDecisao,
  salvarPesos,
} from './squad-metrics';

const ICONE_FAIXA = {
  saudavel: CircleCheck,
  atencao: CircleAlert,
  serio: AlertTriangle,
  critico: OctagonAlert,
} as const;

/** Selo de status: sempre ícone + rótulo, nunca a cor sozinha. */
function SeloFaixa({ indice }: { indice: number }) {
  const faixa = faixaDoIndice(indice);
  const Icone = ICONE_FAIXA[faixa.id];
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-schmersal-gray-dark dark:text-white">
      <Icone className="h-3.5 w-3.5 shrink-0" style={{ color: faixa.cor }} aria-hidden />
      {faixa.rotulo}
    </span>
  );
}

interface LinhaTooltip {
  cor?: string;
  nome: string;
  valor: string;
}

function CaixaTooltip({ titulo, linhas }: { titulo: string; linhas: LinhaTooltip[] }) {
  const { theme } = useTheme();
  const viz = obterVizTheme(theme);
  return (
    <div
      className="rounded-md border px-3 py-2 shadow-lg text-xs"
      style={{ background: viz.tooltipFundo, borderColor: viz.tooltipBorda }}
    >
      <p className="font-semibold mb-1.5" style={{ color: viz.textoPrimario }}>
        {titulo}
      </p>
      {linhas.map((linha) => (
        <div key={linha.nome} className="flex items-center gap-2 py-0.5">
          {linha.cor && <span className="w-3 h-0.5 rounded-full shrink-0" style={{ background: linha.cor }} />}
          {/* Valor primeiro e forte: quem passa o mouse já sabe a série, quer o número. */}
          <span className="font-semibold tabular-nums" style={{ color: viz.textoPrimario }}>
            {linha.valor}
          </span>
          <span style={{ color: viz.textoSecundario }}>{linha.nome}</span>
        </div>
      ))}
    </div>
  );
}

export function SquadOverviewPage() {
  const { theme } = useTheme();
  const { isAdmin, user } = useAuth();
  const viz = obterVizTheme(theme);

  const [periodo, setPeriodo] = useState<Periodo>(PERIODO_ATUAL);
  const [pesos, setPesos] = useState<Pesos>(() => obterPesos());
  const [decisoes, setDecisoes] = useState<Record<string, Decisao>>(() => obterDecisoes());
  const [mostrarPesos, setMostrarPesos] = useState(false);

  const visao = useMemo(() => montarVisao(periodo, pesos), [periodo, pesos]);
  const historico = useMemo(() => montarHistorico(pesos), [pesos]);

  const indiceAnterior = useMemo(() => {
    const idx = PERIODOS.indexOf(periodo);
    if (idx <= 0) return null;
    return montarVisao(PERIODOS[idx - 1], pesos);
  }, [periodo, pesos]);

  const aplicarDecisao = useCallback((id: string, decisao: Decisao) => {
    setDecisoes(salvarDecisao(id, decisao));
  }, []);

  const ajustarPeso = (id: keyof Pesos, valor: number) => {
    const proximos = { ...pesos, [id]: valor };
    setPesos(proximos);
    salvarPesos(proximos);
  };

  const restaurarPesos = () => {
    setPesos(PESOS_PADRAO);
    salvarPesos(PESOS_PADRAO);
  };

  // ---- Agregados de topo -------------------------------------------------
  const indiceMedio = visao.reduce((s, v) => s + v.indice, 0) / visao.length;
  const indiceMedioAnterior = indiceAnterior
    ? indiceAnterior.reduce((s, v) => s + v.indice, 0) / indiceAnterior.length
    : null;
  const deltaIndice = indiceMedioAnterior === null ? null : indiceMedio - indiceMedioAnterior;

  const emRisco = visao.filter((v) => v.indice < 60).length;
  const alocacaoMedia = visao.reduce((s, v) => s + v.avaliacao.alocacaoMedia, 0) / visao.length;
  const desvioBudgetTotal = visao.reduce((s, v) => s + v.desvioBudget, 0);
  const backlogTotal = visao.reduce((s, v) => s + v.avaliacao.backlogItens, 0);
  const pessoasTotal = visao.reduce((s, v) => s + v.avaliacao.headcount, 0);

  const faixaGeral = faixaDoIndice(indiceMedio);
  const IconeGeral = ICONE_FAIXA[faixaGeral.id];

  const totalPaineis = 6;
  const mantidos = Object.values(decisoes).filter((d) => d === 'manter').length;
  const cortados = Object.values(decisoes).filter((d) => d === 'cortar').length;

  // ---- Dados por gráfico -------------------------------------------------
  const dadosIndice = visao
    .slice()
    .sort((a, b) => a.indice - b.indice)
    .map((v) => ({ nome: v.squad.nome, indice: Number(v.indice.toFixed(1)), faixa: v.faixa }));

  const dadosPilares = visao.map((v) => ({
    nome: v.squad.nome,
    capacidade: Number(v.scores.capacidade.toFixed(1)),
    entrega: Number(v.scores.entrega.toFixed(1)),
    custo: Number(v.scores.custo.toFixed(1)),
    pessoas: Number(v.scores.pessoas.toFixed(1)),
  }));

  const dadosQuadrante = visao.map((v, i) => ({
    nome: v.squad.nome,
    custoPorEntrega: Number((v.custoPorEntrega / 1000).toFixed(1)),
    entregasNoPrazo: v.avaliacao.entregasNoPrazo,
    entregasConcluidas: v.avaliacao.entregasConcluidas,
    headcount: v.avaliacao.headcount,
    cor: viz.serie(i),
  }));

  const custoMedio = dadosQuadrante.reduce((s, d) => s + d.custoPorEntrega, 0) / dadosQuadrante.length;
  const prazoMedio = dadosQuadrante.reduce((s, d) => s + d.entregasNoPrazo, 0) / dadosQuadrante.length;

  const dadosAlocacao = visao
    .slice()
    .sort((a, b) => a.desvioAlocacao - b.desvioAlocacao)
    .map((v) => ({ nome: v.squad.nome, desvio: v.desvioAlocacao }));

  const eixoY = {
    tick: { fill: viz.textoSuave, fontSize: 12 },
    axisLine: { stroke: viz.eixo },
    tickLine: false,
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-schmersal-gray-dark dark:text-white">Overview dos Squads</h1>
          <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mt-1">
            Diagnóstico para o plano de reestruturação. Cada painel responde a uma pergunta — marque{' '}
            <strong>Manter</strong> no que for útil e <strong>Cortar</strong> no que não for.
          </p>
        </div>
        <Link
          to="/squads/avaliacao"
          className="inline-flex items-center px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark transition-colors shrink-0"
        >
          <PencilLine className="h-4 w-4 mr-2" />
          Preencher avaliação
        </Link>
      </div>

      {/* Aviso de dados de demonstração — nada aqui saiu do banco. */}
      <div className="flex items-start gap-3 rounded-lg border border-schmersal-yellow bg-schmersal-yellow/10 p-4">
        <FlaskConical className="h-5 w-5 text-schmersal-yellow-dark shrink-0 mt-0.5" aria-hidden />
        <div className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light">
          <strong className="text-schmersal-gray-dark dark:text-white">Dados de demonstração.</strong> Nenhum número
          vem do banco. RJ/ES e MG são squads reais do sistema; SP e PR/SC são hipóteses para dar comparação. O que o
          líder preencher fica salvo só neste navegador. A ideia é decidir <em>o que medir</em> antes de modelar tabela.
        </div>
      </div>

      {/* Filtros: uma linha, acima de tudo que eles filtram. */}
      <div className="flex flex-wrap items-end gap-4 bg-white dark:bg-schmersal-gray-darker rounded-lg shadow p-4 border border-rg-line dark:border-schmersal-gray-dark">
        <div>
          <label
            htmlFor="filtro-periodo"
            className="block text-xs font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light mb-1"
          >
            Período
          </label>
          <select
            id="filtro-periodo"
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value as Periodo)}
            className="px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white text-sm"
          >
            {PERIODOS.map((p) => (
              <option key={p} value={p}>
                {p}
                {p === PERIODO_ATUAL ? ' (atual)' : ''}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={() => setMostrarPesos((v) => !v)}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark transition-colors"
          aria-pressed={mostrarPesos}
        >
          <SlidersHorizontal className="h-4 w-4" />
          Pesos do índice
        </button>

        <div className="ml-auto text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
          <span className="font-semibold text-schmersal-gray-dark dark:text-white tabular-nums">{mantidos}</span> a
          manter ·{' '}
          <span className="font-semibold text-schmersal-gray-dark dark:text-white tabular-nums">{cortados}</span> a
          cortar ·{' '}
          <span className="tabular-nums">{totalPaineis - mantidos - cortados}</span> sem decisão
        </div>
      </div>

      {/* Pesos do índice */}
      {mostrarPesos && (
        <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow p-5 border border-rg-line dark:border-schmersal-gray-dark">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="font-semibold text-schmersal-gray-dark dark:text-white">Pesos do Índice de Saúde</h2>
              <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mt-0.5">
                O índice é uma escolha, não uma verdade. Mexa nos pesos e veja o ranking mudar — se ele vira de cabeça
                para baixo com pouco ajuste, é sinal de que os squads estão empatados de verdade.
              </p>
            </div>
            <button
              type="button"
              onClick={restaurarPesos}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border border-schmersal-gray-light dark:border-schmersal-gray-dark text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark shrink-0"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Padrão
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PILARES.map((pilar) => (
              <div key={pilar.id}>
                <div className="flex items-baseline justify-between mb-1">
                  <label htmlFor={`peso-${pilar.id}`} className="text-sm font-medium text-schmersal-gray-dark dark:text-white">
                    {pilar.nome}
                  </label>
                  <span className="text-sm tabular-nums text-schmersal-gray-medium dark:text-schmersal-gray-light">
                    {pesos[pilar.id]}%
                  </span>
                </div>
                <input
                  id={`peso-${pilar.id}`}
                  type="range"
                  min={0}
                  max={60}
                  step={5}
                  value={pesos[pilar.id]}
                  onChange={(e) => ajustarPeso(pilar.id, Number(e.target.value))}
                  className="w-full accent-schmersal-blue dark:accent-schmersal-turquoise"
                />
                <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light mt-1">
                  {pilar.descricao}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Número-guia da tela */}
      <div className="bg-schmersal-blue rounded-lg shadow-sm p-6 text-white border border-schmersal-blue-light">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm text-schmersal-gray opacity-90">Índice de Saúde da operação · {periodo}</p>
            <div className="flex items-baseline gap-3 mt-1">
              <span className="text-5xl font-semibold leading-none">{indiceMedio.toFixed(0)}</span>
              <span className="text-lg text-schmersal-gray opacity-90">/ 100</span>
              {deltaIndice !== null && (
                <span className="text-sm text-schmersal-gray opacity-90 tabular-nums">
                  {deltaIndice >= 0 ? '+' : ''}
                  {deltaIndice.toFixed(1)} vs. período anterior
                </span>
              )}
            </div>
            <p className="inline-flex items-center gap-1.5 text-sm mt-2 text-white">
              <IconeGeral className="h-4 w-4" aria-hidden />
              {faixaGeral.rotulo} · {emRisco} de {visao.length} squads abaixo de 60
            </p>
          </div>
          <p className="text-sm text-schmersal-gray max-w-md">
            Média dos {visao.length} squads ponderada pelos pesos atuais ({PILARES.map((p) => `${p.nome} ${pesos[p.id]}%`).join(' · ')}).
          </p>
        </div>
      </div>

      {/* Indicadores de apoio */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {[
          {
            titulo: 'Pessoas nos squads',
            valor: String(pessoasTotal),
            apoio: `${(pessoasTotal / visao.length).toFixed(1)} por squad em média`,
          },
          {
            titulo: 'Alocação média',
            valor: `${alocacaoMedia.toFixed(0)}%`,
            apoio: alocacaoMedia > 100 ? 'Acima da faixa saudável (85–100%)' : 'Dentro da faixa saudável (85–100%)',
          },
          {
            titulo: 'Desvio de budget',
            valor: formatarMoedaCompacta(desvioBudgetTotal),
            apoio: desvioBudgetTotal > 0 ? 'Consumido acima do planejado' : 'Consumido abaixo do planejado',
          },
          {
            titulo: 'Backlog acumulado',
            valor: String(backlogTotal),
            apoio: `${(backlogTotal / pessoasTotal).toFixed(1)} itens por pessoa`,
          },
        ].map((tile) => (
          <div
            key={tile.titulo}
            className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-sm p-5 border border-rg-line dark:border-schmersal-gray-dark"
          >
            <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">{tile.titulo}</p>
            <p className="text-3xl font-semibold text-schmersal-gray-dark dark:text-white mt-1">{tile.valor}</p>
            <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light mt-1">{tile.apoio}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 1. Índice por squad */}
        <PainelCard
          id="indice-por-squad"
          titulo="Índice de Saúde por squad"
          pergunta="Qual squad exige decisão primeiro?"
          decisao={decisoes['indice-por-squad'] ?? 'indefinido'}
          onDecisao={aplicarDecisao}
          tabela={{
            colunas: ['Squad', 'Índice', 'Situação'],
            linhas: dadosIndice.map((d) => [d.nome, d.indice.toFixed(1), d.faixa.rotulo]),
          }}
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={dadosIndice} layout="vertical" margin={{ top: 4, right: 44, left: 8, bottom: 4 }}>
              <CartesianGrid horizontal={false} stroke={viz.grade} />
              <XAxis type="number" domain={[0, 100]} {...eixoY} />
              <YAxis type="category" dataKey="nome" width={56} {...eixoY} />
              <Tooltip
                cursor={{ fill: viz.grade, fillOpacity: 0.3 }}
                content={({ active, payload }) =>
                  active && payload?.length ? (
                    <CaixaTooltip
                      titulo={String(payload[0].payload.nome)}
                      linhas={[
                        { cor: payload[0].payload.faixa.cor, nome: payload[0].payload.faixa.rotulo, valor: `${payload[0].value}` },
                      ]}
                    />
                  ) : null
                }
              />
              <Bar dataKey="indice" barSize={22} radius={[0, 4, 4, 0]} isAnimationActive={false}>
                {dadosIndice.map((d) => (
                  <Cell key={d.nome} fill={d.faixa.cor} />
                ))}
                <LabelList
                  dataKey="indice"
                  position="right"
                  offset={8}
                  fill={viz.textoPrimario}
                  fontSize={12}
                  formatter={(v) => Number(v).toFixed(0)}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          {/* Legenda de status: ícone + rótulo, para a cor nunca carregar sozinha. */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 pt-3 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark">
            {FAIXAS.slice().reverse().map((faixa) => {
              const Icone = ICONE_FAIXA[faixa.id];
              return (
                <span
                  key={faixa.id}
                  className="inline-flex items-center gap-1.5 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light"
                >
                  <Icone className="h-3.5 w-3.5" style={{ color: faixa.cor }} aria-hidden />
                  {faixa.rotulo}
                  {faixa.minimo > -Infinity ? ` (${faixa.minimo}+)` : ' (<45)'}
                </span>
              );
            })}
          </div>
        </PainelCard>

        {/* 2. Pilares */}
        <PainelCard
          id="pilares"
          titulo="Os quatro pilares, squad a squad"
          pergunta="O problema é tamanho, processo, custo ou gente?"
          decisao={decisoes['pilares'] ?? 'indefinido'}
          onDecisao={aplicarDecisao}
          tabela={{
            colunas: ['Squad', ...PILARES.map((p) => p.nome)],
            linhas: dadosPilares.map((d) => [d.nome, d.capacidade, d.entrega, d.custo, d.pessoas]),
          }}
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={dadosPilares} margin={{ top: 4, right: 8, left: 0, bottom: 4 }} barGap={2}>
              <CartesianGrid vertical={false} stroke={viz.grade} />
              <XAxis dataKey="nome" {...eixoY} />
              <YAxis domain={[0, 100]} {...eixoY} />
              <Tooltip
                cursor={{ fill: viz.grade, fillOpacity: 0.3 }}
                content={({ active, payload, label }) =>
                  active && payload?.length ? (
                    <CaixaTooltip
                      titulo={String(label)}
                      linhas={payload.map((p) => ({
                        cor: p.color,
                        nome: PILARES.find((pi) => pi.id === p.dataKey)?.nome ?? String(p.dataKey),
                        valor: Number(p.value).toFixed(0),
                      }))}
                    />
                  ) : null
                }
              />
              <Legend
                wrapperStyle={{ fontSize: 12, color: viz.textoSecundario, paddingTop: 8 }}
                formatter={(value) => (
                  <span style={{ color: viz.textoSecundario }}>
                    {PILARES.find((p) => p.id === value)?.nome ?? value}
                  </span>
                )}
              />
              {PILARES.map((pilar, i) => (
                <Bar
                  key={pilar.id}
                  dataKey={pilar.id}
                  fill={viz.serie(i)}
                  barSize={14}
                  radius={[4, 4, 0, 0]}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </PainelCard>

        {/* 3. Quadrante custo × entrega */}
        <PainelCard
          id="quadrante"
          titulo="Custo por entrega × pontualidade"
          pergunta="Quem entrega caro e ainda atrasa?"
          decisao={decisoes['quadrante'] ?? 'indefinido'}
          onDecisao={aplicarDecisao}
          tabela={{
            colunas: ['Squad', 'Custo por entrega', 'Entregas no prazo', 'Entregas concluídas', 'Pessoas'],
            linhas: dadosQuadrante.map((d) => [
              d.nome,
              `R$ ${d.custoPorEntrega.toFixed(1)}k`,
              `${d.entregasNoPrazo}%`,
              d.entregasConcluidas,
              d.headcount,
            ]),
          }}
        >
          <ResponsiveContainer width="100%" height={260}>
            <ScatterChart margin={{ top: 12, right: 24, left: 0, bottom: 16 }}>
              <CartesianGrid stroke={viz.grade} />
              {/* Sem unit="k" no eixo: o rótulo já diz "R$ mil" — os dois juntos viram "40k mil". */}
              <XAxis
                type="number"
                dataKey="custoPorEntrega"
                name="Custo por entrega"
                domain={[0, 100]}
                ticks={[0, 25, 50, 75, 100]}
                {...eixoY}
                label={{
                  value: 'Custo por entrega (R$ mil)',
                  position: 'insideBottom',
                  offset: -8,
                  fill: viz.textoSuave,
                  fontSize: 11,
                }}
              />
              <YAxis
                type="number"
                dataKey="entregasNoPrazo"
                name="No prazo"
                unit="%"
                domain={[40, 100]}
                ticks={[40, 60, 80, 100]}
                {...eixoY}
              />
              <ZAxis range={[150, 150]} />
              <ReferenceLine x={custoMedio} stroke={viz.eixo} />
              <ReferenceLine y={prazoMedio} stroke={viz.eixo} />
              <Tooltip
                cursor={{ stroke: viz.eixo }}
                content={({ active, payload }) =>
                  active && payload?.length ? (
                    <CaixaTooltip
                      titulo={String(payload[0].payload.nome)}
                      linhas={[
                        { nome: 'custo por entrega', valor: `R$ ${payload[0].payload.custoPorEntrega.toFixed(1)}k` },
                        { nome: 'no prazo', valor: `${payload[0].payload.entregasNoPrazo}%` },
                      ]}
                    />
                  ) : null
                }
              />
              {/* Forma explícita: o ponto pintado tem 14px (o mínimo legível) e vem com
                  anel da superfície; o círculo transparente atrás dá ~24px de alvo de
                  toque, para ninguém ter que acertar o miolo do ponto. */}
              <Scatter
                data={dadosQuadrante}
                isAnimationActive={false}
                shape={({ cx, cy, payload }: ScatterShapeProps) => (
                  <g>
                    <circle cx={cx} cy={cy} r={12} fill="transparent" />
                    <circle
                      cx={cx}
                      cy={cy}
                      r={7}
                      fill={(payload as (typeof dadosQuadrante)[number]).cor}
                      stroke={viz.superficie}
                      strokeWidth={2}
                    />
                  </g>
                )}
              >
                <LabelList dataKey="nome" position="top" offset={12} fill={viz.textoPrimario} fontSize={12} />
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light mt-3">
            As linhas cruzam na média. Canto inferior direito = caro e atrasado: é ali que a reestruturação paga.
          </p>
        </PainelCard>

        {/* 4. Alocação */}
        <PainelCard
          id="alocacao"
          titulo="Alocação vs. 100% da capacidade"
          pergunta="Alguém está afogado enquanto outro sobra?"
          decisao={decisoes['alocacao'] ?? 'indefinido'}
          onDecisao={aplicarDecisao}
          tabela={{
            colunas: ['Squad', 'Alocação', 'Desvio'],
            linhas: visao.map((v) => [
              v.squad.nome,
              `${v.avaliacao.alocacaoMedia}%`,
              `${v.desvioAlocacao > 0 ? '+' : ''}${v.desvioAlocacao} p.p.`,
            ]),
          }}
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={dadosAlocacao} layout="vertical" margin={{ top: 4, right: 44, left: 8, bottom: 4 }}>
              <CartesianGrid horizontal={false} stroke={viz.grade} />
              <XAxis type="number" domain={[-45, 45]} ticks={[-45, -30, -15, 0, 15, 30, 45]} {...eixoY} />
              <YAxis type="category" dataKey="nome" width={56} {...eixoY} />
              <ReferenceLine x={0} stroke={viz.eixo} />
              <Tooltip
                cursor={{ fill: viz.grade, fillOpacity: 0.3 }}
                content={({ active, payload }) =>
                  active && payload?.length ? (
                    <CaixaTooltip
                      titulo={String(payload[0].payload.nome)}
                      linhas={[
                        {
                          nome: Number(payload[0].value) > 0 ? 'acima da capacidade' : 'abaixo da capacidade',
                          valor: `${Number(payload[0].value) > 0 ? '+' : ''}${payload[0].value} p.p.`,
                        },
                      ]}
                    />
                  ) : null
                }
              />
              <Bar dataKey="desvio" barSize={22} radius={2} isAnimationActive={false}>
                {dadosAlocacao.map((d) => (
                  <Cell key={d.nome} fill={d.desvio > 0 ? viz.divergenteNegativo : viz.divergentePositivo} />
                ))}
                <LabelList
                  dataKey="desvio"
                  position="right"
                  offset={8}
                  fill={viz.textoPrimario}
                  fontSize={12}
                  formatter={(v) => `${Number(v) > 0 ? '+' : ''}${v}`}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light mt-3">
            Em pontos percentuais sobre 100%. À direita, time acima da capacidade; à esquerda, capacidade sobrando.
            Os dois lados custam dinheiro.
          </p>
        </PainelCard>

        {/* 5. Evolução */}
        <PainelCard
          id="evolucao"
          titulo="Evolução do índice"
          pergunta="Isso é um trimestre ruim ou uma queda sustentada?"
          decisao={decisoes['evolucao'] ?? 'indefinido'}
          onDecisao={aplicarDecisao}
          className="lg:col-span-2"
          tabela={{
            colunas: ['Período', ...SQUADS_DEMO.map((s) => s.nome)],
            linhas: historico.map((linha) => [
              String(linha.periodo),
              ...SQUADS_DEMO.map((s) => Number(linha[s.id]).toFixed(1)),
            ]),
          }}
        >
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={historico} margin={{ top: 8, right: 56, left: 0, bottom: 4 }}>
              <CartesianGrid vertical={false} stroke={viz.grade} />
              <XAxis dataKey="periodo" {...eixoY} />
              {/* Os ticks são os próprios cortes de situação: a grade vira a régua de
                  leitura, em vez de linhas de referência indistinguíveis da grade. */}
              <YAxis domain={[0, 100]} ticks={[0, 45, 60, 75, 100]} {...eixoY} />
              <Tooltip
                cursor={{ stroke: viz.eixo }}
                content={({ active, payload, label }) =>
                  active && payload?.length ? (
                    <CaixaTooltip
                      titulo={String(label)}
                      linhas={payload.map((p) => ({
                        cor: p.color,
                        nome: SQUADS_DEMO.find((s) => s.id === p.dataKey)?.nome ?? String(p.dataKey),
                        valor: Number(p.value).toFixed(0),
                      }))}
                    />
                  ) : null
                }
              />
              <Legend
                wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                formatter={(value) => (
                  <span style={{ color: viz.textoSecundario }}>
                    {SQUADS_DEMO.find((s) => s.id === value)?.nome ?? value}
                  </span>
                )}
              />
              {SQUADS_DEMO.map((squad, i) => (
                <Line
                  key={squad.id}
                  type="monotone"
                  dataKey={squad.id}
                  stroke={viz.serie(i)}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 5, strokeWidth: 2, stroke: viz.superficie }}
                  isAnimationActive={false}
                >
                  {/* Rótulo só na ponta da linha: nome ao lado do último ponto, e nada nos demais. */}
                  <LabelList
                    position="right"
                    offset={10}
                    fontSize={12}
                    fill={viz.textoPrimario}
                    valueAccessor={(_entrada, indice) => (indice === historico.length - 1 ? squad.nome : '')}
                  />
                </Line>
              ))}
            </LineChart>
          </ResponsiveContainer>
          <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light mt-3">
            As marcas do eixo são os cortes de situação: abaixo de 45 é crítico, 60 é o limite de atenção e 75 é
            saudável.
          </p>
        </PainelCard>

        {/* 6. Diagnóstico do líder */}
        <PainelCard
          id="diagnostico"
          titulo="O que os líderes escreveram"
          pergunta="O número diz onde dói; o líder diz por quê."
          decisao={decisoes['diagnostico'] ?? 'indefinido'}
          onDecisao={aplicarDecisao}
          className="lg:col-span-2"
          tabela={{
            colunas: ['Squad', 'Índice', 'Gargalo', 'Ação proposta'],
            linhas: visao.map((v) => [v.squad.nome, v.indice.toFixed(1), v.avaliacao.gargalos, v.avaliacao.acaoProposta]),
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {visao
              .slice()
              .sort((a, b) => a.indice - b.indice)
              .map((v) => (
                <div
                  key={v.squad.id}
                  className="rounded-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark p-4"
                >
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <h4 className="font-semibold text-schmersal-gray-dark dark:text-white truncate">
                        {v.squad.nome}
                      </h4>
                      {!v.squad.real && (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-schmersal-gray-light dark:border-schmersal-gray-dark text-schmersal-gray-medium dark:text-schmersal-gray-light shrink-0">
                          hipótese
                        </span>
                      )}
                    </div>
                    <SeloFaixa indice={v.indice} />
                  </div>
                  <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                    {v.avaliacao.headcount} pessoas · {v.avaliacao.alocacaoMedia}% alocado ·{' '}
                    {formatarMoeda(v.avaliacao.budgetConsumido)} de {formatarMoeda(v.avaliacao.budgetPlanejado)}
                  </p>
                  <p className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light mt-3">
                    <strong className="text-schmersal-gray-dark dark:text-white">Gargalo:</strong> {v.avaliacao.gargalos}
                  </p>
                  <p className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light mt-2">
                    <strong className="text-schmersal-gray-dark dark:text-white">Proposta:</strong>{' '}
                    {v.avaliacao.acaoProposta}
                  </p>
                  {!v.preenchidaPeloLider && (
                    <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light mt-3 italic">
                      Texto de demonstração — o líder ainda não preencheu este período.
                    </p>
                  )}
                </div>
              ))}
          </div>
        </PainelCard>
      </div>

      {!isAdmin && user?.squadNome && (
        <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
          Você está vendo todos os squads porque este painel ainda roda com dados de demonstração. Ligado ao banco, um
          líder do squad {user.squadNome} veria apenas o próprio squad.
        </p>
      )}
    </div>
  );
}
